import type { PropsWithChildren } from 'react';
import { Children, isValidElement, useState } from 'react';

import { Button } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';

import { Box, Stack } from '@packages/fenrir/components';

interface ViewMoreProps {
  max: number;
  isVertical?: boolean;
}

export const ViewMore = ({
  children,
  max,
  isVertical,
}: PropsWithChildren<ViewMoreProps>) => {
  const { t } = useTranslation('ui');
  const [isViewingMore, setIsViewingMore] = useState(false);

  const extraComponentCount = Math.max(0, Children.count(children) - max);

  const componentCountToDisplay = isViewingMore
    ? Children.count(children)
    : max;

  const direction = isVertical ? 'column' : 'row';

  return (
    <Stack
      direction={direction}
      spacing={1}
      sx={{
        alignItems: isVertical ? 'flex-start' : undefined,
        flexWrap: 'wrap',
      }}
    >
      <Box
        component="ul"
        display="flex"
        flexDirection={direction}
        gap={1}
        flexWrap="wrap"
      >
        {Children.toArray(children)
          .slice(0, componentCountToDisplay)
          .map((child, index) => {
            const { key } = isValidElement(child) ? child : { key: null };
            return (
              <Box key={key ?? index} component="li">
                {child}
              </Box>
            );
          })}
      </Box>

      {extraComponentCount > 0 &&
        (isViewingMore ? (
          <Button variant="link" onClick={() => setIsViewingMore(false)}>
            {t('viewLess')}
          </Button>
        ) : (
          <Button variant="link" onClick={() => setIsViewingMore(true)}>
            {`${t('viewXMore', {
              count: extraComponentCount,
            })}`}
          </Button>
        ))}
    </Stack>
  );
};
